import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useApi } from '../../../hooks/useApi';
import { useStores } from '../../../hooks/useStore';
import { DealsFirstTagListRequest } from '../../../api/analytics/dto/DealsFirstTagListRequest.dto';
import { Deal } from '../../../api/analytics/dto/DealsFirstTagListResponse.dto';
import styles from './DealsModal.module.css';

interface DealsModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    tag?: string;
    bucket?: 'new' | 'old' | 'repeat';
    responsible?: string;
    source?: string;
    medium?: string;
    campaign?: string;
  };
  title: string;
  completionDateFilters?: {
    useCompletionDate: boolean;
    completionDateFrom: string;
    completionDateTo: string;
  };
}

const DealsModal: React.FC<DealsModalProps> = observer(
  ({ isOpen, onClose, filters, title, completionDateFilters }) => {
    const { dateStore } = useStores();
    const api = useApi();

    const [deals, setDeals] = useState<Deal[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const ITEMS_PER_PAGE = 20;

    const loadDeals = async (resetData: boolean = false) => {
      if (!dateStore.startDate || !dateStore.endDate) {
        return;
      }

      setLoading(true);

      try {
        const currentOffset = resetData ? 0 : offset;

        const request: DealsFirstTagListRequest = {
          filters: {
            date_from: dateStore.startDate,
            date_to: dateStore.endDate,
            ...(completionDateFilters?.useCompletionDate &&
              completionDateFilters.completionDateFrom &&
              completionDateFilters.completionDateTo && {
                date_from_completed: completionDateFilters.completionDateFrom,
                date_to_completed: completionDateFilters.completionDateTo,
              }),
            only_not_archived: true,
            only_not_deleted: true,
          },
          parent: filters,
          pagination: {
            limit: ITEMS_PER_PAGE,
            offset: currentOffset,
          },
          sort: {
            field: 'created_date',
            order: 'desc',
          },
        };

        const response = await api.analytics.getDealsFirstTagList(request);

        if (
          response.success &&
          response.body &&
          Array.isArray(response.body.items)
        ) {
          const newDeals = response.body.items.filter(
            (deal) => deal && typeof deal === 'object',
          );

          if (resetData) {
            setDeals(newDeals);
            setOffset(newDeals.length);
          } else {
            setDeals((prev) => [...(prev || []), ...newDeals]);
            setOffset((prev) => prev + newDeals.length);
          }

          setTotal(response.body.total || 0);
          setHasMore(newDeals.length === ITEMS_PER_PAGE);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading deals:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadMoreDeals = () => {
      if (!loading && hasMore) {
        loadDeals(false);
      }
    };

    const handleDealClick = (dealId: string | null | undefined) => {
      if (!dealId) {
        return;
      }
      const crmUrl = `https://admin.accelonline.io/pipelines/zzOuyiDLHE6wMvBfsOUekQ/deal/${dealId}`;
      window.open(crmUrl, '_blank');
    };

    const formatDate = (dateString: string | null | undefined): string => {
      if (!dateString) {
        return '-';
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return '-';
        }
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return '-';
      }
    };

    const formatMoney = (amount: number | null | undefined): string => {
      if (amount === null || amount === undefined || isNaN(amount)) {
        return '-';
      }
      return `${Math.round(amount).toLocaleString('ru-RU')} $`;
    };

    useEffect(() => {
      if (isOpen) {
        setOffset(0);
        setHasMore(true);
        loadDeals(true);
      }
    }, [isOpen, filters, dateStore.startDate, dateStore.endDate]);

    if (!isOpen) {
      return null;
    }

    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.summary}>
            <span>Всего сделок: {total || 0}</span>
          </div>

          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.headerCell}>ID сделки</div>
              <div className={styles.headerCell}>Контакт</div>
              <div className={styles.headerCell}>Ответственный</div>
              <div className={styles.headerCell}>Создано</div>
              <div className={styles.headerCell}>Завершено</div>
              <div className={styles.headerCell}>Доход</div>
              <div className={styles.headerCell}>Стадия</div>
            </div>

            <div className={styles.tableBody}>
              {deals && deals.length > 0
                ? deals.map((deal, index) => (
                    <div
                      key={deal.deal_id || `deal-${index}`}
                      className={styles.tableRow}
                      onClick={() => handleDealClick(deal.deal_id)}
                    >
                      <div className={styles.cell}>{deal.deal_id || '-'}</div>
                      <div className={styles.cell}>
                        {deal.contact_name || '-'}
                      </div>
                      <div className={styles.cell}>
                        {deal.responsible_name || '-'}
                      </div>
                      <div className={styles.cell}>
                        {formatDate(deal.created_date)}
                      </div>
                      <div className={styles.cell}>
                        {formatDate(deal.completed_date)}
                      </div>
                      <div className={styles.cell}>
                        {formatMoney(deal.revenue)}
                      </div>
                      <div className={styles.cell}>
                        {deal.stage_name || '-'}
                      </div>
                    </div>
                  ))
                : null}

              {loading && <div className={styles.loader}>Загрузка...</div>}

              {hasMore && !loading && deals && deals.length > 0 && (
                <button
                  className={styles.loadMoreButton}
                  onClick={loadMoreDeals}
                >
                  Загрузить ещё
                </button>
              )}

              {(!deals || deals.length === 0) && !loading && (
                <div className={styles.emptyState}>Сделки не найдены</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default DealsModal;
